// Petites briques visuelles partagées par la liste et le détail des Tickets : badge d'auteur,
// pastille d'état, chip de catégorie, horodatage relatif, titre de section. Uniquement des classes
// Tailwind littérales (slate/blanc + marque #005CA9) : pas de token shadcn, ils rendent transparent
// dans cet admin.
import React from "react";
import { Link } from "react-router-dom";
import { formatExactDateFr, formatRelativeTimeFr } from "../../Helper/dates";
import { UserAvatar } from "../../Components/UserAvatar";
import { categoryTone } from "./ticketUi";
import type { TicketAuthor } from "./ticketsClient";

/** Avatar + nom de l'auteur (cliquable → /account/{id} quand le profil est résolu), avec repli
 *  texte brut (e-mail / « Système ») pour les auteurs historiques non résolus. */
export const AuthorBadge: React.FC<{ author?: TicketAuthor | null; fallback?: string | null; sizeClass?: string }> = ({ author, fallback, sizeClass = "h-5 w-5" }) => {
	const name = author?.name || fallback || "Système";
	const id = author?.id ?? undefined;
	const avatar = <UserAvatar name={name} id={id} avatarKey={author?.avatarKey} pictureUrl={author?.pictureUrl} sizeClass={sizeClass} textClass="text-[9px]" />;
	return (
		<span className="inline-flex items-center gap-1.5 align-middle">
			{avatar}
			{id ? (
				<Link to={`/account/${id}`} className="font-semibold text-slate-600 transition hover:text-sky-700 hover:underline">{name}</Link>
			) : (
				<span className="font-semibold text-slate-600">{name}</span>
			)}
		</span>
	);
};

/** Pastille d'état avec point de couleur : émeraude vivant pour un ticket ouvert, ardoise pour un fermé. */
export const StatusPill: React.FC<{ status: string }> = ({ status }) =>
	status === "closed" ? (
		<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
			<span className="h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden /> Fermé
		</span>
	) : (
		<span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
			<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden /> Ouvert
		</span>
	);

/** Chip de catégorie coloré : une couleur stable par catégorie (voir ticketUi.categoryTone). */
export const CategoryChip: React.FC<{ categoryKey: string; label: string }> = ({ categoryKey, label }) => (
	<span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${categoryTone(categoryKey).chip}`}>
		{label}
	</span>
);

/** Horodatage relatif fr-FR, la date exacte en infobulle. */
export const RelativeTime: React.FC<{ value?: string | null; className?: string }> = ({ value, className }) => (
	<time dateTime={value ?? undefined} title={formatExactDateFr(value)} className={className}>
		{formatRelativeTimeFr(value)}
	</time>
);

/** Petit titre de section du panneau de détail (icône + libellé + compteur éventuel). */
export const SectionLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode; trailing?: React.ReactNode }> = ({ icon, children, trailing }) => (
	<p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
		{icon} {children}
		{trailing && <span className="ml-auto normal-case tracking-normal">{trailing}</span>}
	</p>
);

/** Squelette d'une carte pendant le chargement de la liste. */
export const CardSkeleton: React.FC = () => (
	<div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
		<div className="flex animate-pulse items-start gap-3">
			<div className="h-10 w-10 rounded-full bg-slate-100" />
			<div className="flex-1 space-y-2.5">
				<div className="h-3.5 w-2/3 rounded bg-slate-100" />
				<div className="flex gap-1.5">
					<div className="h-4 w-16 rounded-full bg-slate-100" />
					<div className="h-4 w-20 rounded-full bg-slate-100" />
				</div>
				<div className="h-3 w-1/3 rounded bg-slate-100" />
			</div>
		</div>
	</div>
);
